<template>
  <li class="nav-item">
    <!-- Gear trigger (sits with the other right-side navbar items). The
         feather-icon uses `text-body` exactly like the notification/calendar
         icons so it follows the theme (dark on light navbar, light on dark). -->
    <b-link class="nav-link" v-b-tooltip.hover :title="$t('settingsPanel.title')" @click="show = true">
      <feather-icon icon="SettingsIcon" size="21" class="text-body"/>
    </b-link>

    <!-- Slide-out panel from the right -->
    <b-sidebar
        id="crm-settings-sidebar"
        v-model="show"
        right
        shadow
        backdrop
        no-header
        width="330px"
        sidebar-class="crm-settings"
    >
      <div class="crm-settings__head">
        <div>
          <h5 class="mb-0"><i class="fa-solid fa-sliders mr-1"></i>{{ $t('settingsPanel.title') }}</h5>
          <small class="text-muted">{{ $t('settingsPanel.subtitle') }}</small>
        </div>
        <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="show = false">
          <feather-icon icon="XIcon" size="18"/>
        </b-button>
      </div>

      <div class="crm-settings__body">
        <!-- ===== Font size ===== -->
        <div class="crm-settings__section">
          <label class="crm-settings__label">{{ $t('settingsPanel.fontSize') }}</label>
          <div class="crm-settings__fonts">
            <button
                v-for="opt in fontOptions"
                :key="opt.key"
                type="button"
                class="crm-settings__font"
                :class="{ active: fontScale === opt.scale }"
                @click="selectFont(opt)"
            >
              <span :style="{ fontSize: opt.preview }">Aa</span>
              <small>{{ $t(opt.label) }}</small>
            </button>
          </div>
        </div>

        <!-- ===== Dark mode theme colour ===== -->
        <div class="crm-settings__section">
          <label class="crm-settings__label">{{ $t('settingsPanel.darkThemeColor') }}</label>
          <small class="text-muted d-block mb-1">{{ $t('settingsPanel.darkThemeHint') }}</small>
          <div class="crm-settings__swatches">
            <button
                v-for="opt in darkThemes"
                :key="opt.key"
                type="button"
                class="crm-settings__swatch"
                :class="{ active: darkTheme === opt.key }"
                v-b-tooltip.hover
                :title="$t(opt.label)"
                :style="{ background: opt.surface, borderColor: opt.bg }"
                @click="selectDarkTheme(opt)"
            >
              <span class="crm-settings__swatch-dot" :style="{ background: opt.bg }"></span>
              <feather-icon v-if="darkTheme === opt.key" icon="CheckIcon" size="14" class="crm-settings__check"/>
            </button>
          </div>
        </div>

        <b-button variant="outline-secondary" size="sm" block class="mt-1" @click="resetDefaults">
          <i class="fa-solid fa-rotate-left mr-1"></i>{{ $t('settingsPanel.reset') }}
        </b-button>
      </div>
    </b-sidebar>
  </li>
</template>

<script>
// localStorage keys
const LS_FONT = 'crm.fontScale';
const LS_DARK = 'crm.darkTheme';

const FONT_OPTIONS = [
  { key: 'sm', label: 'settingsPanel.sizes.small', scale: 90, preview: '0.8rem' },
  { key: 'md', label: 'settingsPanel.sizes.default', scale: 100, preview: '1rem' },
  { key: 'lg', label: 'settingsPanel.sizes.large', scale: 110, preview: '1.2rem' },
  { key: 'xl', label: 'settingsPanel.sizes.xlarge', scale: 120, preview: '1.4rem' },
];

// surface = card/menu/navbar colour, surface2 = striped/hover, bg = app canvas
const DARK_THEMES = [
  { key: 'navy', label: 'settingsPanel.colors.navy', bg: '#1a2130', surface: '#232c3e', surface2: '#2e3850' },
  { key: 'slate', label: 'settingsPanel.colors.slate', bg: '#1b1f26', surface: '#272c36', surface2: '#313845' },
  { key: 'graphite', label: 'settingsPanel.colors.graphite', bg: '#17191c', surface: '#23272e', surface2: '#2d323c' },
  { key: 'black', label: 'settingsPanel.colors.black', bg: '#0c0e12', surface: '#15181f', surface2: '#1e222b' },
  { key: 'ocean', label: 'settingsPanel.colors.ocean', bg: '#0f1f29', surface: '#173040', surface2: '#1f3e52' },
  { key: 'teal', label: 'settingsPanel.colors.teal', bg: '#0e1f20', surface: '#15302f', surface2: '#1d403e' },
  { key: 'forest', label: 'settingsPanel.colors.forest', bg: '#121d16', surface: '#1c2c22', surface2: '#25392c' },
  { key: 'indigo', label: 'settingsPanel.colors.indigo', bg: '#15172e', surface: '#20233f', surface2: '#2a2e52' },
  { key: 'plum', label: 'settingsPanel.colors.plum', bg: '#1b1626', surface: '#271f34', surface2: '#332843' },
  { key: 'wine', label: 'settingsPanel.colors.wine', bg: '#20141a', surface: '#2e1d26', surface2: '#3a2531' },
  { key: 'coffee', label: 'settingsPanel.colors.coffee', bg: '#1c1813', surface: '#2a241c', surface2: '#362e23' },
];

export default {
  name: 'AppSettings',
  data: () => ({
    show: false,
    fontScale: 100,
    darkTheme: 'navy',
    fontOptions: FONT_OPTIONS,
    darkThemes: DARK_THEMES,
  }),
  created() {
    // Load saved preferences and apply them on every page load.
    const savedScale = parseInt(localStorage.getItem(LS_FONT), 10);
    if (!Number.isNaN(savedScale)) this.fontScale = savedScale;
    const savedDark = localStorage.getItem(LS_DARK);
    if (savedDark) this.darkTheme = savedDark;

    this.applyFontScale(this.fontScale);
    this.applyDarkTheme(this.darkTheme);
  },
  methods: {
    selectFont(opt) {
      this.fontScale = opt.scale;
      localStorage.setItem(LS_FONT, String(opt.scale));
      this.applyFontScale(opt.scale);
    },
    selectDarkTheme(opt) {
      this.darkTheme = opt.key;
      localStorage.setItem(LS_DARK, opt.key);
      this.applyDarkTheme(opt.key);
    },
    resetDefaults() {
      this.selectFont(FONT_OPTIONS.find(o => o.scale === 100));
      this.selectDarkTheme(DARK_THEMES[0]);
    },
    applyFontScale(scale) {
      const html = document.documentElement;
      // Capture the stylesheet's natural root size once, so repeated applies
      // never compound and "Default" returns to exactly the original size.
      if (this._baseFontPx == null) {
        html.style.fontSize = '';
        this._baseFontPx = parseFloat(getComputedStyle(html).fontSize) || 16;
      }
      if (scale === 100) {
        html.style.fontSize = '';
      } else {
        html.style.fontSize = (this._baseFontPx * scale / 100) + 'px';
      }
    },
    applyDarkTheme(key) {
      const theme = DARK_THEMES.find(t => t.key === key) || DARK_THEMES[0];
      const body = document.body;
      // 'navy' is the stock dark theme — clear all overrides so the native
      // look is untouched. Only the opt-in presets tint the surfaces.
      if (theme.key === 'navy') {
        body.style.removeProperty('--crm-dark-bg');
        body.style.removeProperty('--crm-dark-surface');
        body.style.removeProperty('--crm-dark-surface-2');
        body.removeAttribute('data-crm-dark-theme');
        return;
      }
      body.style.setProperty('--crm-dark-bg', theme.bg);
      body.style.setProperty('--crm-dark-surface', theme.surface);
      body.style.setProperty('--crm-dark-surface-2', theme.surface2);
      body.setAttribute('data-crm-dark-theme', theme.key);
    },
  },
};
</script>

<style scoped>
.crm-settings__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(34, 41, 47, 0.08);
}
.crm-settings__body {
  padding: 1rem 1.25rem;
}
.crm-settings__section {
  margin-bottom: 1.5rem;
}
.crm-settings__label {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  display: block;
}

/* Font size picker */
.crm-settings__fonts {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.crm-settings__font {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 64px;
  border: 1px solid rgba(34, 41, 47, 0.16);
  border-radius: 10px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.crm-settings__font small { font-size: 0.66rem; }
.crm-settings__font:hover { border-color: #007bff; }
.crm-settings__font.active {
  border-color: #007bff;
  background: rgba(0, 123, 255, 0.08);
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.18);
}

/* Dark theme colour swatches */
.crm-settings__swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.crm-settings__swatch {
  position: relative;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  border: 2px solid;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform .12s ease, box-shadow .12s ease;
}
.crm-settings__swatch:hover { transform: translateY(-2px); }
.crm-settings__swatch.active { box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.55); }
.crm-settings__swatch-dot {
  position: absolute;
  right: 5px;
  bottom: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
}
.crm-settings__check { color: #fff; }

/* Dark mode adjustments for the panel itself */
.dark-layout .crm-settings__head { border-color: rgba(255, 255, 255, 0.10); }
.dark-layout .crm-settings__font { border-color: rgba(255, 255, 255, 0.16); }
</style>
